import { RefObject } from "react";

import { css } from "@emotion/react";

import { trackEvent } from "interfaces/tracker";

import { useAlert } from "contexts/alertContext";

const style = {
  input: css({ display: "none" }),
};

type Props = {
  isDisabled?: boolean;
  onChange: (files: File[]) => void;
  imageFileInputRef?: RefObject<HTMLInputElement>;
  acceptedFileTypes: string;
  maxSizeInMb: number;
  multiple?: boolean;
  fileTypeString?: string;
};

export const FileInput = ({
  onChange,
  imageFileInputRef,
  isDisabled,
  acceptedFileTypes,
  maxSizeInMb,
  multiple,
  fileTypeString,
}: Props) => {
  const { createAlert } = useAlert();

  return (
    <input
      aria-label="File Input"
      ref={imageFileInputRef}
      css={style.input}
      type="file"
      accept={acceptedFileTypes}
      multiple={multiple}
      onChange={async (e) => {
        const files = Array.from(e.target?.files || []);

        if (!files?.length) return;

        if (files.some((file) => !acceptedFileTypes.includes(file.type))) {
          return createAlert(
            "error",
            `File${files.length > 1 ? "s" : ""} must be ${
              fileTypeString || acceptedFileTypes
            }`
          );
        }
        const fileTooBig = files.find(
          (file) => file.size > maxSizeInMb * 1024 * 1024
        );
        if (fileTooBig) {
          // track the event in mixpanel
          trackEvent("FileTooLarge", {
            fileSize: fileTooBig.size,
            maxSizeInMb,
            fileType: fileTooBig.type,
            error: `File size is too large. Please try again with a file under ${maxSizeInMb}MB.`,
          });
          return createAlert(
            "error",
            `File size is too large. Please try again with a file under ${maxSizeInMb}MB.`
          );
        }

        onChange(multiple ? files : files.slice(0, 1));

        // resetting the value allows user to upload the same image twice.
        // otherwise onchange would not trigger as it would see the same value as before
        e.target.value = "";
      }}
      disabled={isDisabled}
    />
  );
};
